import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';

export default function Home() {
  const { currentUser, userData } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'products'), where('active', '==', true));
      const querySnapshot = await getDocs(q);
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setProducts(productsData);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(productsData.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const formatPrice = (product) => {
    let finalPrice = product.basePrice;
    
    if (product.discount?.type === 'percentage' && product.discount?.value) {
      finalPrice = finalPrice * (1 - product.discount.value / 100);
    } else if (product.discount?.type === 'fixed' && product.discount?.value) {
      finalPrice = Math.max(0, finalPrice - product.discount.value);
    }
    
    return finalPrice;
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <h1>Welcome to Our Store</h1>
        <p>Discover amazing products tailored just for you</p>
        {currentUser && (
          <p className="user-welcome">Welcome back, {userData?.username || currentUser.email}!</p>
        )}
      </header>
      
      <main className="page-content">
        {/* Category Filter */}
        <div className="category-filter">
          <button 
            className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All Products
          </button>
          {categories.map(category => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>No products found in this category.</p>
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.title}
                      className="product-thumbnail"
                    />
                  ) : (
                    <div className="no-product-image">No Image</div>
                  )}
                </div>
                
                <div className="product-info">
                  <h3 className="product-title">{product.title}</h3>
                  <p className="product-description">{product.description}</p>
                  
                  {product.category && (
                    <span className="product-category">{product.category}</span>
                  )}
                  
                  <div className="product-price">
                    <span className="price">${formatPrice(product).toFixed(2)}</span>
                    {product.discount?.type !== 'none' && (
                      <span className="original-price">${product.basePrice.toFixed(2)}</span>
                    )}
                  </div>
                  
                  {product.options && product.options.length > 0 && (
                    <div className="product-options">
                      <p className="options-label">Options available:</p>
                      {product.options.map((option, index) => (
                        <span key={index} className="option-tag">
                          {option.name}: {option.choices.join(', ')}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="product-actions">
                    {currentUser ? (
                      <Link 
                        to={`/product/${product.id}`} 
                        className="shop-now-btn"
                      >
                        Shop Now
                      </Link>
                    ) : (
                      <div className="login-required">
                        <p>Login required to purchase</p>
                        <Link to="/login" className="login-link">
                          Login / Register
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
